import debug from "debug";
import {loggerConfig, loggerNamespaces, LogNamespaceType} from "../../config/logger";
import {LoggerServiceNamespaceRegistry} from "./logger-service-namespace-registry";
import type {LoggerServiceNamespace} from "./logger-service-namespace";
import {proxyLoggerService} from "./logger-service-proxy";
import {localStorageService} from "../storage/local-storage";
import storageKeys from "../storage/storageKeys";
import {ILogArguments} from "./ILogArguments";

/**
 * This file is built on top of https://github.com/debug-js/debug
*/
class LoggerService {

  constructor() {
    // log as started
    this.debug({message: `started`});
  }

  public enableLoggingForAllNamespaces(): void {
    const allNamespaces = Object.values(loggerNamespaces).join(',');
    debug.enable(allNamespaces);
    this.info({message: 'Enabling logging for all namespaces'});
    localStorageService.setItem(storageKeys.logging_enabledNamespaces, allNamespaces);
  }

  public getEnabledNamespaces(): string {
    const enabledNamespaces = localStorageService.getItem(storageKeys.logging_enabledNamespaces);
    if (!enabledNamespaces) {
      return "";
    }

    return enabledNamespaces;
  }

  public disabledLoggingForAllNamespaces(): void {
    this.info({message: 'Disabling logging for all namespaces'});
    debug.enable("");
    localStorageService.setItem(storageKeys.logging_enabledNamespaces, "");
  }

  public toggleLoggingForNamespace(namespace: LogNamespaceType) {
    const enabledNamespaces = this.getEnabledNamespaces();
    const enabledNamespacesArray = enabledNamespaces.split(',');

    if (enabledNamespacesArray.includes(namespace)) {
      enabledNamespacesArray.splice(enabledNamespacesArray.indexOf(namespace), 1);
      this.info({message: `Disabling logging for namespace: ${namespace}`});
    } else {
      enabledNamespacesArray.push(namespace);
      this.info({message: `Enabling logging for namespace: ${namespace}`});
    }

    const newSetOfEnabledNamespaces = enabledNamespacesArray.join(',');
    debug.enable(newSetOfEnabledNamespaces);
  }

  public isLoggingEnabledForNamespace(namespace: LogNamespaceType): boolean {
    const enabledNamespaces = this.getEnabledNamespaces().split(',');
    return enabledNamespaces.includes(namespace);
  }

  private getNamespaceFromRegistry(namespace: LogNamespaceType): LoggerServiceNamespace {
    return LoggerServiceNamespaceRegistry.get(namespace);
  }

  public getNamespace(namespace: LogNamespaceType): LoggerServiceNamespace {
    return this.getNamespaceFromRegistry(namespace);
  }

  private getDefaultNamespace(): LoggerServiceNamespace {
    return this.getNamespaceFromRegistry(loggerConfig.namespaces.root);
  }

  public debug({message, showStack, data, showDataOnOneLine, additionalNote}: ILogArguments) {
    this.getDefaultNamespace().debug({message, showStack, data, showDataOnOneLine, additionalNote});
  }
  public info({message, showStack, data, showDataOnOneLine, additionalNote}: ILogArguments){
    this.getDefaultNamespace().info({message, showStack, data, showDataOnOneLine, additionalNote});
  }

  public log({message, showStack, data, showDataOnOneLine, additionalNote}: ILogArguments){
    this.getDefaultNamespace().log({message, showStack, data, showDataOnOneLine, additionalNote});
  }
  public warn({message, showStack, data, showDataOnOneLine, additionalNote}: ILogArguments){
    this.getDefaultNamespace().warn({message, showStack, data, showDataOnOneLine, additionalNote});
  }
  public error({message, showStack, data, showDataOnOneLine, additionalNote, error}: ILogArguments){
    this.getDefaultNamespace().error({message, showStack, data, showDataOnOneLine, additionalNote, error});
  }

  public setCallStackGlobalValue(value: "true" | "false" | "deferred") {
    localStorageService.setItem<string>(storageKeys.logging_global_showStacks, JSON.stringify(value));
  }

  public setDataGlobalValue(value: "true" | "false" | "deferred") {
    localStorageService.setItem<string>(storageKeys.logging_global_showDataOnOneLine, JSON.stringify(value));
  }
}

export type LoggerServiceType = LoggerService;

export const logger = proxyLoggerService(new LoggerService());
